import React from 'react';
import { compose } from 'redux';
import ReactDOM from 'react-dom';
import classnames from 'classnames';
import throttle from 'lodash/throttle';
import { withTranslation, WithTranslation } from 'react-i18next';
import reduxConnectContainer, {
  ConnectedKeys,
} from './WidgetBoxWide.container';
import WidgetHeaderWide from '../WidgetHeaderWide';
import WidgetHeaderMobile from '../WidgetHeaderMobile';
import WidgetEditControls from '../WidgetEditControls';
import Button from '@wix/da-shared-react/pkg/Button';
import { ModuleType } from '../../../../../types/modules';
import { BiLoggerContextProvider } from '@wix/da-shared-react/pkg/biLogger';

import s from './WidgetBoxWide.scss';
import ErrorBoundary from '@wix/da-shared-react/pkg/ErrorBoundary';
import { withMobileContext } from '@wix/da-shared-react/pkg/Context/MobileContext';

export interface Props {
  moduleId: number;
  moduleType: ModuleType;
  moduleVersion: number;
  id: string;
  header: string | React.ReactNode;
  count?: number | string;
  subHeader?: string;
  autoShowWidgetConfig?: boolean;
  headerExtraElementsOnMobile?: React.ReactNode;
  isHeaderStickyOnMobile?: boolean;
  isCurrentUserProfileOwner?: boolean;
  isNewlyAddedModule?: boolean;
  hasMore?: boolean;
  fetchMore?: () => void;
  isFetching?: boolean;
  isEmpty?: boolean;
  emptyView?: React.ReactNode;
  className?: string;
  widgetBoxClassName?: string;
  children: React.ReactNode;
  widgetMenuRenderer?: (closeMenu) => React.ReactNode;
  widgetMoveUp?: () => void;
  widgetMoveDown?: () => void;
  widgetRemove?: () => void;
  editControlsTooltipTitle?: string;
  editControlsTooltipContent?: React.ReactNode;
  updateModule?: (payload: any) => void;
  parentRef: any;
}

export interface State {
  isWidgetConfigShown: boolean;
  splitWidgetInto2Columns: boolean;
}

const SCROLL_THROTTLE_TIMEOUT = 200;
const WINDOW_WIDTH_TO_SPLIT_INTO_2_COLUMN = 1600;
const SECOND_CHILD_HEIGHT_TO_SPLIT_INTO_2_COLUMN = 250;

const enhance = compose(
  reduxConnectContainer,
  withMobileContext,
  withTranslation()
);
class WidgetBoxWide extends React.PureComponent<
  Props & WithTranslation & { isMobile?: boolean },
  State
> {
  state = {
    isWidgetConfigShown: false,
    splitWidgetInto2Columns: false,
  };
  private secondChildRef: React.ReactInstance | null;

  componentDidMount() {
    const children = React.Children.toArray(this.props.children);
    if (children.length > 1) {
      this.maybeSplitInto2Column();
      window.addEventListener('resize', this.maybeSplitInto2Column);
    }
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.maybeSplitInto2Column);
  }

  render() {
    const { props } = this;

    if (props.isEmpty && !props.emptyView) {
      return null;
    }

    const { splitWidgetInto2Columns } = this.state;
    const children = React.Children.toArray(this.props.children);
    const firstChild = props.isEmpty ? props.emptyView : children[0];
    return (
      <BiLoggerContextProvider
        value={{ widgetname: props.moduleType, sectionname: props.moduleType }}
      >
        <div
          id={props.id}
          className={classnames(s['root'], props.className)}
          ref={props.parentRef}
        >
          <ErrorBoundary>
            <div className={s['col-left']}>
              {props.isMobile ? (
                <WidgetHeaderMobile
                  header={props.header}
                  count={props.count}
                  isSticky={props.isHeaderStickyOnMobile}
                  extras={props.headerExtraElementsOnMobile}
                />
              ) : (
                <WidgetHeaderWide
                  header={props.header}
                  subHeader={props.subHeader}
                  extras={
                    props.isCurrentUserProfileOwner && this.renderEditControls()
                  }
                />
              )}
              {splitWidgetInto2Columns && firstChild}
            </div>
            <div className={s['col-right']}>
              {splitWidgetInto2Columns ? (
                this.renderSecondChild()
              ) : (
                <>
                  {firstChild}
                  <span />
                  {this.renderSecondChild()}
                </>
              )}
              {!props.isEmpty && props.hasMore && (
                <Button
                  view="b8"
                  disabled={props.isFetching}
                  className={s['view-more-button']}
                  onClick={props.fetchMore}
                >
                  {props.t!('common.loadMore')}
                </Button>
              )}
            </div>
          </ErrorBoundary>
        </div>
      </BiLoggerContextProvider>
    );
  }

  renderSecondChild() {
    const children = React.Children.toArray(this.props.children);
    const secondChild = children[1] as React.ReactElement<any>;
    return (
      secondChild &&
      React.cloneElement(secondChild, {
        ref: el => (this.secondChildRef = el),
      })
    );
  }

  renderEditControls() {
    return (
      <div className={s['edit-widget-controls-wrapper']}>
        <WidgetEditControls
          className={s['edit-widget-controls']}
          onMoveUp={this.props.widgetMoveUp}
          onMoveDown={this.props.widgetMoveDown}
          onRemove={this.props.widgetRemove}
          onEdit={this.props.widgetMenuRenderer && this.showWidgetConfig}
          tooltipTitle={this.props.editControlsTooltipTitle}
          tooltipContent={this.props.editControlsTooltipContent}
        />
        {this.renderWidgetConfig()}
      </div>
    );
  }

  maybeSplitInto2Column = throttle(
    () => {
      let secondChildHeight = 0;
      if (this.secondChildRef) {
        const node = ReactDOM.findDOMNode(
          this.secondChildRef
        ) as Element | null;
        if (node) {
          secondChildHeight = node.getBoundingClientRect().height;
        }
      }
      const splitWidgetInto2Columns =
        !this.props.isEmpty &&
        window.innerWidth > WINDOW_WIDTH_TO_SPLIT_INTO_2_COLUMN &&
        secondChildHeight > SECOND_CHILD_HEIGHT_TO_SPLIT_INTO_2_COLUMN;
      this.setState({ splitWidgetInto2Columns });
    },
    SCROLL_THROTTLE_TIMEOUT,
    {
      leading: true,
    }
  );

  renderWidgetConfig() {
    const {
      widgetMenuRenderer,
      autoShowWidgetConfig,
      isNewlyAddedModule,
    } = this.props;
    const { isWidgetConfigShown } = this.state;

    if (!widgetMenuRenderer) {
      return null;
    }

    if (isWidgetConfigShown || (isNewlyAddedModule && autoShowWidgetConfig)) {
      return widgetMenuRenderer && widgetMenuRenderer(this.hideWidgetConfig);
    }
  }

  showWidgetConfig = () => {
    this.setState({ isWidgetConfigShown: true });
  };

  hideWidgetConfig = () => {
    const { isNewlyAddedModule, updateModule } = this.props;
    this.setState({ isWidgetConfigShown: false });
    isNewlyAddedModule &&
      updateModule &&
      updateModule({
        id: this.props.moduleId,
        moduleMeta: {
          isNewlyAdded: false,
        },
      });
  };
}

export default enhance(WidgetBoxWide) as React.ComponentType<
  Without<Props, ConnectedKeys>
>;
